/**
 * Admin Dashboard form components.
 * Extracted to a separate module to avoid ReferenceErrors from bundling/hoisting.
 *
 * IMPORTANT: All Cloud Function calls use ../lib/api only (httpsCallable).
 * There is no fetch(), axios, or direct cloudfunctions.net URL in this file.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  createClass,
  updateClass,
  deleteClass,
  listStudentsForAdmin,
  getClassesWithEnrollments,
  getStudentWithEnrollments,
  createEnrollment,
  deleteEnrollment,
  listRegistrations,
  listTransactions,
  markRegistrationPaidManually,
  getClassSelectionWindow,
  setClassSelectionWindow,
  getActiveRegistrationMonth,
  setActiveRegistrationMonth,
  getAdminSettings,
  updateAdminSettings,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import type {
  StudentForAdmin,
  RegistrationRecord,
  StudentPaymentRecord,
  TransactionRecord,
  StudentEnrollmentRow,
  AdminSettings,
} from "../types";
import { exportEstudiantesToExcel, exportStudentsToExcel, exportTransactionsToExcel } from "../lib/excelExport";
import {
  Plus,
  ClipboardList,
  Search,
  Clock,
  Users,
  Edit2,

  Trash2,
  ChevronRight,
  ChevronLeft,
  Mail,
  Download,
  Phone,
  User as UserIcon,
  Heart,
  CreditCard,
  BookOpen,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

// Shared admin section UI – compact card (smaller padding and type)
const sectionContainer = "bg-white rounded-2xl shadow-sm border border-black/[0.02] overflow-hidden transition-all duration-300";
const sectionPadding = "p-5";
const sectionTitle = "text-xl font-bold text-[#161b3d] mb-0.5";
const sectionDescription = "text-xs text-gray-400 font-medium";
const inputLabel = "text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1";
const inputClass = "w-full bg-gray-50/50 border border-gray-100 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all";
const btnPrimary = "bg-[#5856d6] hover:bg-[#4a48b8] text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-[0.98] shadow-md shadow-indigo-100 disabled:opacity-50 disabled:active:scale-100";
const subsectionTitle = "text-base font-bold text-[#161b3d]";

/** Max students shown in the "Ver todos los estudiantes" table (then "refine filter" message). */
const MAX_STUDENTS_DISPLAY = 50;

/** Classes per page in admin "Todas las clases" list. */
const CLASSES_PER_PAGE = 6;

/** Admin UI: show registration status in Spanish. Firestore keeps English values. Status is display-only; do not allow manual editing or reverting paid → pending. */
const adminStatusMap: Record<string, string> = {
  awaiting_payment: "Pendiente de pago",
  awaiting_manual_payment: "Pendiente de pago (manual)",
  paid: "Pagado",
  completed: "Pagado",
  declined: "Rechazado",
  cancelled: "Cancelado",
};

function adminStatusLabel(status: string): string {
  return adminStatusMap[status] ?? status;
}

// =============================================================================
// CLASSES (list + create + edit + enable/disable)
// =============================================================================

export function AdminClassesView() {
  const { firebaseUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    classes: Array<{ id: string; name: string; monthlyPrice: number; isActive: boolean; scheduleDay?: string; scheduleTime?: string; description?: string; cupos?: number; maxCapacity?: number; currentEnrollment?: number }>;
    enrollmentsByClassId: Record<string, Array<{ enrollmentId: string; childName: string; month: string }>>;
  } | null>(null);
  const [expandedClassId, setExpandedClassId] = useState<string | null>(null);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editScheduleDay, setEditScheduleDay] = useState("");
  const [editScheduleTime, setEditScheduleTime] = useState("");
  const [editCupos, setEditCupos] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [createName, setCreateName] = useState("");
  const [createPrice, setCreatePrice] = useState("");
  const [createScheduleDay, setCreateScheduleDay] = useState("");
  const [createScheduleTime, setCreateScheduleTime] = useState("");
  const [createCupos, setCreateCupos] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createMessage, setCreateMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [classSelectionOpen, setClassSelectionOpenState] = useState<boolean | null>(null);
  const [classSelectionLoading, setClassSelectionLoading] = useState(false);
  const [classSelectionError, setClassSelectionError] = useState<string | null>(null);
  const [classesPage, setClassesPage] = useState(1);
  const [classesSearchQuery, setClassesSearchQuery] = useState("");
  const [activationModalClass, setActivationModalClass] = useState<{
    id: string; name: string; scheduleDay?: string; scheduleTime?: string; cupos?: number; maxCapacity?: number;
  } | null>(null);
  const [activationCapacity, setActivationCapacity] = useState("");
  const [activationScheduleDay, setActivationScheduleDay] = useState("");
  const [activationScheduleTime, setActivationScheduleTime] = useState("");
  const [activationSubmitting, setActivationSubmitting] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getClassesWithEnrollments()
      .then((res) => setData({ classes: res.classes, enrollmentsByClassId: res.enrollmentsByClassId }))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load classes."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Load class selection window flag for Pagos screen
    (async () => {
      try {
        const res = await getClassSelectionWindow();
        setClassSelectionOpenState(res.open);
      } catch (err) {
        setClassSelectionError(
          err instanceof Error ? err.message : "No se pudo cargar el estado de selección de clases."
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!editingClassId) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEditingClassId(null);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [editingClassId]);

  const handleToggleClassSelection = async () => {
    if (!firebaseUser || classSelectionOpen === null) return;
    setClassSelectionLoading(true);
    setClassSelectionError(null);
    try {
      const res = await setClassSelectionWindow(!classSelectionOpen);
      setClassSelectionOpenState(res.open);
    } catch (err) {
      setClassSelectionError(
        err instanceof Error ? err.message : "No se pudo actualizar la selección de clases."
      );
    } finally {
      setClassSelectionLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateMessage(null);
    const price = parseFloat(createPrice);
    if (!createName.trim() || isNaN(price) || price <= 0) {
      setCreateMessage({ type: "err", text: "Nombre y un precio mensual positivo son obligatorios." });
      return;
    }
    setCreateSubmitting(true);
    try {
      const cuposNum = createCupos.trim() ? Math.max(0, Math.floor(parseFloat(createCupos) || 0)) : undefined;
      await createClass({
        name: createName.trim(),
        monthlyPrice: price,
        isActive: true,
        ...(createScheduleDay.trim() && { scheduleDay: createScheduleDay.trim() }),
        ...(createScheduleTime.trim() && { scheduleTime: createScheduleTime.trim() }),
        ...(createDescription.trim() && { description: createDescription.trim() }),
        ...(cuposNum !== undefined && cuposNum > 0 && { cupos: cuposNum }),
      });
      setCreateMessage({ type: "ok", text: `Clase creada: ${createName}` });
      setCreateName("");
      setCreatePrice("");
      setCreateScheduleDay("");
      setCreateScheduleTime("");
      setCreateCupos("");
      setCreateDescription("");
      load();
    } catch (err: unknown) {
      setCreateMessage({ type: "err", text: err instanceof Error ? err.message : "Error al crear la clase." });
    } finally {
      setCreateSubmitting(false);
    }
  };

  const startEdit = (c: { id: string; name: string; monthlyPrice: number; scheduleDay?: string; scheduleTime?: string; description?: string; cupos?: number }) => {
    setEditingClassId(c.id);
    setEditName(c.name);
    setEditPrice(String(c.monthlyPrice));
    setEditScheduleDay(c.scheduleDay ?? "");
    setEditScheduleTime(c.scheduleTime ?? "");
    setEditCupos(c.cupos != null && c.cupos > 0 ? String(c.cupos) : "");
    setEditDescription(c.description ?? "");
  };

  const handleUpdate = async (e: React.FormEvent, classId: string) => {
    e.preventDefault();
    const price = parseFloat(editPrice);
    if (!editName.trim() || isNaN(price) || price <= 0) {
      return;
    }
    setUpdateSubmitting(true);
    try {
      const cuposNum = editCupos.trim() ? Math.max(0, Math.floor(parseFloat(editCupos) || 0)) : 0;
      await updateClass({
        classId,
        name: editName.trim(),
        monthlyPrice: price,
        scheduleDay: editScheduleDay.trim(),
        scheduleTime: editScheduleTime.trim(),
        description: editDescription.trim(),
        cupos: cuposNum,
      });
      setEditingClassId(null);
      load();
    } catch (err: unknown) {
      // could set a small error state per row
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const handleToggleActive = (classId: string, isActive: boolean) => {
    // Deactivating: no validation needed
    if (isActive) {
      updateClass({ classId, isActive: false }).then(load).catch(() => {});
      return;
    }
    // Activating: require maxCapacity, scheduleDay, scheduleTime
    const cls = data?.classes.find((c) => c.id === classId);
    if (!cls) return;
    const missingCapacity = !(cls.maxCapacity && cls.maxCapacity > 0) && !(cls.cupos && cls.cupos > 0);
    const missingDay      = !cls.scheduleDay?.trim();
    const missingTime     = !cls.scheduleTime?.trim();
    if (missingCapacity || missingDay || missingTime) {
      setActivationModalClass(cls);
      setActivationCapacity(cls.cupos && cls.cupos > 0 ? String(cls.cupos) : "");
      setActivationScheduleDay(cls.scheduleDay ?? "");
      setActivationScheduleTime(cls.scheduleTime ?? "");
      setActivationError(null);
      return;
    }
    updateClass({ classId, isActive: true }).then(load).catch(() => {});
  };

  const handleActivationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activationModalClass) return;
    const cuposNum = Math.floor(parseFloat(activationCapacity) || 0);
    if (cuposNum <= 0) {
      setActivationError("Los cupos máximos deben ser un número mayor a 0.");
      return;
    }
    if (!activationScheduleDay.trim()) {
      setActivationError("El día es obligatorio.");
      return;
    }
    if (!activationScheduleTime.trim()) {
      setActivationError("El horario es obligatorio.");
      return;
    }
    setActivationSubmitting(true);
    setActivationError(null);
    try {
      await updateClass({
        classId:      activationModalClass.id,
        scheduleDay:  activationScheduleDay.trim(),
        scheduleTime: activationScheduleTime.trim(),
        cupos:        cuposNum,
        isActive:     true,
      });
      setActivationModalClass(null);
      load();
    } catch (err: unknown) {
      setActivationError(err instanceof Error ? err.message : "Error al activar la clase.");
    } finally {
      setActivationSubmitting(false);
    }
  };

  const handleDeleteClass = async (classId: string) => {
    if (!window.confirm("¿Eliminar esta clase? Las matrículas que la referencian no se eliminarán.")) return;
    try {
      await deleteClass({ classId });
      load();
    } catch (err: unknown) {
      console.error(err);
    }
  };

  return (
    <div className={`${sectionContainer}`}>
      <div className={`${sectionPadding} space-y-5`}>
        <div>
          <h2 className={sectionTitle}>Clases</h2>
          <p className={sectionDescription}>Crear, editar y habilitar o deshabilitar clases.</p>
        </div>

        {/* Selection Control - compact */}
        <div className="p-4 rounded-xl bg-gray-50/50 border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-[#161b3d]">Selección de clases (familias)</h3>
            <p className="text-[11px] text-gray-400 font-medium mt-0.5">Controla cuándo las familias pueden modificar su selección.</p>
            {classSelectionError && (
              <p className="mt-1 text-xs text-red-600">{classSelectionError}</p>
            )}
          </div>
          <button
            type="button"
            disabled={!firebaseUser || classSelectionLoading || classSelectionOpen === null}
            onClick={handleToggleClassSelection}
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] font-bold text-[#161b3d] hover:bg-gray-50 transition-colors disabled:opacity-50 shrink-0"
          >
            {classSelectionLoading ? "Guardando…" : classSelectionOpen ? "Cerrar selección" : "Abrir selección"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Form - left column lg:col-span-5 */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center">
                <Plus className="w-3.5 h-3.5 text-indigo-600" />
              </div>
              <h3 className="text-base font-bold text-[#161b3d]">Crear clase</h3>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <label className={`block ${inputLabel}`}>Nombre de la clase</label>
                  <input
                    type="text"
                    required
                    placeholder="ej. Taller de arte"
                    className={inputClass}
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Precio mensual ($)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    placeholder="0"
                    className={`${inputClass} pl-7 pr-3 tabular-nums`}
                      value={createPrice}
                      onChange={(e) => setCreatePrice(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Cupos máximos</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Sin límite"
                    className={inputClass}
                    value={createCupos}
                    onChange={(e) => setCreateCupos(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Día</label>
                  <input
                    type="text"
                    placeholder="ej. Lunes, Martes"
                    className={inputClass}
                    value={createScheduleDay}
                    onChange={(e) => setCreateScheduleDay(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Horario</label>
                  <input
                    type="text"
                    placeholder="ej. 10:00 – 11:30"
                    className={inputClass}
                    value={createScheduleTime}
                    onChange={(e) => setCreateScheduleTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Descripción (opcional)</label>
                <textarea
                  placeholder="Descripción de la clase..."
                  rows={2}
                  className={`${inputClass} resize-none`}
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                />
              </div>
              {createMessage && (
                <p className={`text-sm font-medium ${createMessage.type === "ok" ? "text-green-600" : "text-red-600"}`}>{createMessage.text}</p>
              )}
              <button type="submit" disabled={createSubmitting || !firebaseUser} className={`w-full ${btnPrimary}`}>
                {createSubmitting ? "Creando…" : "Crear clase"}
              </button>
            </form>
          </div>

          {/* List - right column lg:col-span-7 */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gray-50 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-3.5 h-3.5 text-gray-400" />
                </div>
                <h3 className="text-base font-bold text-[#161b3d]">Todas las clases</h3>
              </div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                <input
                  type="text"
                  value={classesSearchQuery}
                  onChange={(e) => {
                    setClassesSearchQuery(e.target.value);
                    setClassesPage(1);
                  }}
                  placeholder="Buscar"
                  className="bg-gray-50 border border-gray-100 rounded-lg pl-8 pr-3 py-1.5 text-[11px] focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-200 w-40 min-w-0"
                  aria-label="Buscar clases"
                />
              </div>
            </div>
            {loading ? (
              <p className="text-sm text-gray-400">Cargando…</p>
            ) : error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : !data || data.classes.length === 0 ? (
              <p className="text-sm text-gray-400">Aún no hay clases. Crea una arriba.</p>
            ) : (() => {
              const q = classesSearchQuery.trim().toLowerCase();
              const filteredClasses = q
                ? data.classes.filter((c) => {
                    const name = (c.name ?? "").toLowerCase();
                    const day = (c.scheduleDay ?? "").toLowerCase();
                    const time = (c.scheduleTime ?? "").toLowerCase();
                    const desc = (c.description ?? "").toLowerCase();
                    return name.includes(q) || day.includes(q) || time.includes(q) || desc.includes(q);
                  })
                : data.classes;
              const totalPages = Math.max(1, Math.ceil(filteredClasses.length / CLASSES_PER_PAGE));
              const page = Math.min(classesPage, totalPages);
              const paginatedClasses = filteredClasses.slice((page - 1) * CLASSES_PER_PAGE, page * CLASSES_PER_PAGE);
              const hasSearch = q.length > 0;
              const noResults = hasSearch && filteredClasses.length === 0;
              return (
              <>
              {noResults ? (
                <div className="h-[420px] flex items-center justify-center">
                  <p className="text-sm text-gray-500">Ninguna clase coincide con &quot;{classesSearchQuery.trim()}&quot;</p>
                </div>
              ) : (
              <div className="h-[420px] overflow-hidden flex flex-col">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={page}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="space-y-2 min-h-full"
                  >
                {paginatedClasses.map((c) => {
                  const enrollments = data.enrollmentsByClassId[c.id] ?? [];
                  const isExpanded = expandedClassId === c.id;
                  const scheduleStr = [c.scheduleDay, c.scheduleTime].filter(Boolean).join(" · ") || "TBA";
                  const enrollmentStr = (c.maxCapacity ?? c.cupos) != null && (c.maxCapacity ?? c.cupos)! > 0
                    ? `${c.currentEnrollment ?? 0} / ${(c.maxCapacity ?? c.cupos)!}`
                    : null;
                  return (
                    <div key={c.id} className="rounded-xl border border-gray-50 overflow-hidden hover:border-indigo-100 hover:bg-indigo-50/10 transition-all">
                        <>
                          <div className="group flex items-center gap-3 p-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <button type="button" onClick={() => setExpandedClassId(isExpanded ? null : c.id)} className="text-xs font-bold text-[#161b3d] truncate text-left hover:text-indigo-600 transition-colors">
                                  {c.name}
                                </button>
                                <span className="text-[9px] font-black bg-gray-100 text-gray-400 px-1 py-0.5 rounded uppercase tracking-tighter tabular-nums">
                                  RD$ {c.monthlyPrice.toLocaleString()}
                                </span>
                                {!c.isActive && <span className="text-[9px] font-bold bg-gray-100 text-gray-400 px-1 py-0.5 rounded uppercase">Inactiva</span>}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5 shrink-0" /> {scheduleStr}
                                </p>
                                {enrollmentStr != null && (
                                  <p className="text-[10px] text-indigo-400 font-bold flex items-center gap-1">
                                    <Users className="w-2.5 h-2.5 shrink-0" /> {enrollmentStr}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleActive(c.id, c.isActive)}
                              className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus:outline-none ${c.isActive ? "bg-green-500" : "bg-gray-200"}`}
                              aria-label={c.isActive ? "Desactivar clase" : "Activar clase"}
                              title={c.isActive ? "Activa — clic para desactivar" : "Inactiva — clic para activar"}
                            >
                              <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${c.isActive ? "translate-x-3.5" : "translate-x-0.5"}`} />
                            </button>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                              <button type="button" onClick={() => startEdit(c)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" aria-label="Editar">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" onClick={() => handleDeleteClass(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" aria-label="Eliminar">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-indigo-200 transition-colors shrink-0" />
                          </div>
                          {isExpanded && (
                            <div className="px-3 py-2 border-t border-gray-100 bg-white text-xs text-gray-500 space-y-1.5">
                              {c.description && <p className="text-sm text-[#161b3d] whitespace-pre-wrap">{c.description}</p>}
                              {enrollments.length === 0 ? (
                                <p className="text-sm text-gray-400">Sin matrículas.</p>
                              ) : (
                                <ul className="space-y-1">
                                  {enrollments.map((e) => (
                                    <li key={e.enrollmentId} className="font-medium text-[#161b3d]">{e.childName} ({e.month})</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </>
                    </div>
                  );
                })}
                  </motion.div>
                </AnimatePresence>
              </div>
              )}
              {!noResults && totalPages > 1 && (
                <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
                  <span className="text-[11px] text-gray-500">
                    Página {page} de {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setClassesPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setClassesPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      aria-label="Página siguiente"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Activation — complete missing fields modal */}
      <AnimatePresence>
        {activationModalClass && (
          <motion.div
            key="activation-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            onClick={() => setActivationModalClass(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="activation-modal-title"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-100">
                <h3 id="activation-modal-title" className="text-lg font-bold text-[#161b3d]">Completar antes de activar</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  La clase <span className="font-semibold text-[#161b3d]">{activationModalClass.name}</span> requiere estos campos para poder activarse.
                </p>
              </div>
              <form onSubmit={handleActivationSubmit} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <label className={`block ${inputLabel}`}>Cupos máximos <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="ej. 20"
                      className={inputClass}
                      value={activationCapacity}
                      onChange={(e) => setActivationCapacity(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={`block ${inputLabel}`}>Día <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="ej. Lunes, Martes"
                      className={inputClass}
                      value={activationScheduleDay}
                      onChange={(e) => setActivationScheduleDay(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={`block ${inputLabel}`}>Horario <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="ej. 10:00 – 11:30"
                      className={inputClass}
                      value={activationScheduleTime}
                      onChange={(e) => setActivationScheduleTime(e.target.value)}
                    />
                  </div>
                </div>
                {activationError && (
                  <p className="text-sm font-medium text-red-600">{activationError}</p>
                )}
                <div className="flex items-center gap-4 pt-2">
                  <button type="submit" disabled={activationSubmitting} className={`${btnPrimary} px-4 py-3`}>
                    {activationSubmitting ? "Activando…" : "Guardar y activar"}
                  </button>
                  <button type="button" onClick={() => setActivationModalClass(null)} className="text-sm font-bold text-gray-500 hover:bg-gray-100 px-4 py-3 rounded-xl transition-colors">
                    Cancelar
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit class modal */}
      <AnimatePresence>
        {editingClassId && (
          <motion.div
            key="edit-class-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            onClick={() => setEditingClassId(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-class-title"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="p-5 border-b border-gray-100">
              <h3 id="edit-class-title" className="text-lg font-bold text-[#161b3d]">Editar clase</h3>
              <p className="text-xs text-gray-400 mt-0.5">Modifica los datos y guarda los cambios.</p>
            </div>
            <form
              onSubmit={(e) => handleUpdate(e, editingClassId)}
              className="p-5 space-y-4"
            >
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Nombre de la clase</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="ej. Taller de arte" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Precio mensual ($)</label>
                  <input type="number" step="0.01" min="0" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Cupos máximos</label>
                  <input type="number" min="0" step="1" value={editCupos} onChange={(e) => setEditCupos(e.target.value)} placeholder="Sin límite" className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Día</label>
                  <input type="text" value={editScheduleDay} onChange={(e) => setEditScheduleDay(e.target.value)} placeholder="ej. Lunes, Martes" className={inputClass} />
                </div>
                <div className="space-y-1.5">
                  <label className={`block ${inputLabel}`}>Horario</label>
                  <input type="text" value={editScheduleTime} onChange={(e) => setEditScheduleTime(e.target.value)} placeholder="ej. 10:00 – 11:30" className={inputClass} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Descripción (opcional)</label>
                <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Descripción de la clase..." rows={3} className={`${inputClass} resize-none`} />
              </div>
              <div className="flex items-center gap-4 pt-5">
                <button type="submit" disabled={updateSubmitting} className={`${btnPrimary} px-4 py-3`}>
                  {updateSubmitting ? "Guardando…" : "Guardar"}
                </button>
                <button type="button" onClick={() => setEditingClassId(null)} className="text-sm font-bold text-gray-500 hover:bg-gray-100 px-4 py-3 rounded-xl transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// VIEW REGISTRATIONS (Public form submissions)
// =============================================================================

/** Date in letters + number format for Detalle de inscripción: "12 de Octubre, 2024" */
function formatRegistrationDateLong(createdAt: RegistrationRecord["createdAt"]): string {
  if (!createdAt) return "—";
  const c = createdAt as { seconds?: number; _seconds?: number; toMillis?: () => number };
  const ms =
    typeof c.toMillis === "function"
      ? c.toMillis()
      : (c.seconds ?? c._seconds) != null
        ? (c.seconds ?? c._seconds!) * 1000
        : 0;
  if (!ms) return "—";
  const d = new Date(ms);
  const day = d.getDate();
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  return `${day} de ${month}, ${year}`;
}

const REGISTRATIONS_PAGE_SIZE = 20;

function registrationCreatedAtMonth(reg: RegistrationRecord): string {
  const c = reg.createdAt as { seconds?: number; _seconds?: number; toMillis?: () => number } | null;
  if (!c) return "";
  const ms =
    typeof c.toMillis === "function"
      ? c.toMillis()
      : (c.seconds ?? c._seconds) != null
        ? (c.seconds ?? c._seconds!) * 1000
        : 0;
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function AdminRegistrationsView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<RegistrationRecord[]>([]);
  const [registrationClasses, setRegistrationClasses] = useState<Array<{ id: string; name: string }>>([]);
  const [filterStatus, setFilterStatus] = useState<"pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined" | "">("");
  const [filterPayment, setFilterPayment] = useState<"ONLINE" | "LOCAL" | "">("");
  const [filterFiscal, setFilterFiscal] = useState<"" | "yes" | "no">("");
  const [filterClassId, setFilterClassId] = useState("");
  const [filterDateMonth, setFilterDateMonth] = useState("");
  const [appliedFilterStatus, setAppliedFilterStatus] = useState<"pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined" | "">("");
  const [appliedFilterPayment, setAppliedFilterPayment] = useState<"ONLINE" | "LOCAL" | "">("");
  const [appliedFilterFiscal, setAppliedFilterFiscal] = useState<"" | "yes" | "no">("");
  const [appliedFilterClassId, setAppliedFilterClassId] = useState("");
  const [appliedFilterDateMonth, setAppliedFilterDateMonth] = useState("");
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);
  const [markPaidSuccess, setMarkPaidSuccess] = useState<string | null>(null);
  const [showFiscalDetails, setShowFiscalDetails] = useState(false);

  useEffect(() => {
    if (!markPaidSuccess) return;
    const t = setTimeout(() => setMarkPaidSuccess(null), 5000);
    return () => clearTimeout(t);
  }, [markPaidSuccess]);

  useEffect(() => {
    getClassesWithEnrollments()
      .then((res) => setRegistrationClasses(res.classes))
      .catch(() => setRegistrationClasses([]));
  }, []);

  const load = (overrides?: {
    status?: "pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined" | "";
    payment?: "ONLINE" | "LOCAL" | "";
    classId?: string;
    dateMonth?: string;
    fiscal?: "" | "yes" | "no";
  }) => {
    const status = overrides?.status ?? appliedFilterStatus;
    const payment = overrides?.payment ?? appliedFilterPayment;
    const classId = overrides?.classId ?? appliedFilterClassId;
    const dateMonth = overrides?.dateMonth ?? appliedFilterDateMonth;
    const fiscal = overrides?.fiscal ?? appliedFilterFiscal;
    if (overrides) {
      setAppliedFilterStatus(status);
      setAppliedFilterPayment(payment);
      setAppliedFilterClassId(classId);
      setAppliedFilterDateMonth(dateMonth);
      setAppliedFilterFiscal(fiscal);
    }
    setLoading(true);
    setError(null);
    const filters: { status?: "pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined"; paymentMethod?: "ONLINE" | "LOCAL"; classId?: string } = {};
    if (status) filters.status = status;
    if (payment) filters.paymentMethod = payment;
    if (classId?.trim()) filters.classId = classId.trim();
    listRegistrations(filters)
      .then((res) => {
        let regs = res.registrations;
        // IMPORTANT: use the local fiscal value for this load call,
        // not the (possibly stale) appliedFilterFiscal state.
        if (fiscal === "yes") {
          regs = regs.filter((r) => Boolean(r.fiscalData && (r.fiscalData as any).requireNCF));
        } else if (fiscal === "no") {
          regs = regs.filter((r) => !r.fiscalData || !(r.fiscalData as any).requireNCF);
        }
        setRegistrations(regs);
        setPage(1);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load registrations."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load({
      status: filterStatus,
      payment: filterPayment,
      classId: filterClassId,
      dateMonth: filterDateMonth,
      fiscal: filterFiscal,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPayment, filterClassId, filterDateMonth, filterFiscal]);

  const filteredByDate = useMemo(() => {
    if (!appliedFilterDateMonth.trim()) return registrations;
    return registrations.filter((r) => registrationCreatedAtMonth(r) === appliedFilterDateMonth.trim());
  }, [registrations, appliedFilterDateMonth]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredByDate.length / REGISTRATIONS_PAGE_SIZE)),
    [filteredByDate.length]
  );
  const currentPage = Math.min(page, totalPages);
  const paginatedRegistrations = useMemo(
    () =>
      filteredByDate.slice(
        (currentPage - 1) * REGISTRATIONS_PAGE_SIZE,
        currentPage * REGISTRATIONS_PAGE_SIZE
      ),
    [filteredByDate, currentPage]
  );
  const selectedRegistration = useMemo(
    () => (selectedRegistrationId ? registrations.find((r) => r.id === selectedRegistrationId) ?? null : null),
    [registrations, selectedRegistrationId]
  );

  const exportExcelRecords = useMemo((): StudentPaymentRecord[] => {
    const classMap = new Map(registrationClasses.map((c) => [c.id, c.name]));
    return filteredByDate.map((r) => {
      const classNames = (r.selectedClasses || [])
        .map((id) => classMap.get(id) ?? id)
        .filter(Boolean);
      const className = classNames.length === 0 ? "—" : classNames.length === 1 ? classNames[0]! : classNames.join(", ");
      const month = appliedFilterDateMonth.trim() || registrationCreatedAtMonth(r);
      const parentName = [r.parents?.motherName, r.parents?.fatherName].filter(Boolean).join(" / ") || "—";
      return {
        studentName: r.child?.childName ?? "—",
        parentName,
        parentEmail: r.parents?.parentEmail ?? "—",
        className,
        month,
        amount: r.totalPrice ?? 0,
        paymentStatus: r.status === "paid" || r.status === "completed" ? "Pagado" : "Pendiente",
        paymentDate: r.paymentDate ?? "",
      };
    });
  }, [filteredByDate, registrationClasses, appliedFilterDateMonth]);

  const handleExportExcel = () => {
    if (exportExcelRecords.length === 0) return;
    const firstClass = registrationClasses.find((c) => c.id === appliedFilterClassId)?.name ?? "inscripciones";
    const month = appliedFilterDateMonth.trim() || new Date().toISOString().slice(0, 7);
    exportStudentsToExcel(exportExcelRecords, { className: firstClass, month });
  };

  if (loading && registrations.length === 0) {
    return (
      <div className={sectionContainer}>
        <div className={sectionPadding}>
          <p className="text-[13px] text-[#86868b]">Cargando inscripciones…</p>
        </div>
      </div>
    );
  }
  if (error && registrations.length === 0) {
    return (
      <div className={sectionContainer}>
        <div className={sectionPadding}>
          <p className="text-[13px] text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Separate detail page for one registration
  if (selectedRegistration) {
    const r = selectedRegistration;
    const studentName = r.child?.childName ?? "—";
    const school = r.child?.school ?? "—";
    const ageLabel = r.child?.age ? `${r.child.age} años` : "—";
    const studentCode = r.studentCode ?? "—";

    const parentEmail = r.parents?.parentEmail ?? "—";
    const homePhone = r.parents?.housePhone || "—";
    const motherName = r.parents?.motherName || "—";
    const motherPhone = r.parents?.motherCell || "";
    const fatherName = (r.parents?.fatherName ?? "").trim() || "—";
    const fatherPhone = (r.parents?.fatherCell ?? "").trim() || "—";

    const emergencyContacts = r.emergencyContacts ?? [];
    const classMap = new Map(registrationClasses.map((c) => [c.id, c.name]));
    const selectedClassNames = (r.selectedClasses || []).map((id) => classMap.get(id) ?? id);

    const statusLabel = adminStatusLabel(r.status);
    const isPaid = r.status === "paid" || r.status === "completed";
    const totalLabel = isPaid ? `Total pagado • ${statusLabel}` : `Total • ${statusLabel}`;
    const totalAmount = r.totalPrice ?? 0;
    const hasFiscalData = !!r.fiscalData;

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={`detail-${r.id}`}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
          className={sectionContainer}
        >
          <div className={sectionPadding}>
            <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-base font-bold text-[#161b3d] mb-0.5">Detalle de inscripción</h3>
              <p className="text-[10px] text-[#86868b]">
                Recibida el {formatRegistrationDateLong(r.createdAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowFiscalDetails(false);
                setSelectedRegistrationId(null);
              }}
              className="flex items-center gap-1 text-[11px] font-bold text-[#5856D6] hover:bg-indigo-50 px-2 py-1 rounded-md transition-colors"
            >
              <span aria-hidden>←</span>
              <span>Volver a la lista</span>
            </button>
          </div>

          <div className="space-y-2">
            {/* Información del estudiante */}
            <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="p-0.5 bg-gray-50 rounded">
                  <UserIcon className="w-3 h-3 text-[#5856d6]" />
                </div>
                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                  Información del estudiante
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Nombre completo</p>
                  <p className="text-[12px] font-bold text-[#5856d6] leading-snug">{studentName}</p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Colegio</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{school}</p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Edad</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{ageLabel}</p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Código de estudiante</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{studentCode}</p>
                </div>
              </div>
            </section>

            {/* Información de contacto */}
            <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="p-0.5 bg-gray-50 rounded">
                  <Mail className="w-3 h-3 text-[#5856d6]" />
                </div>
                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                  Información de contacto
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Correo electrónico</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug break-all">{parentEmail}</p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Teléfono de casa</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{homePhone || "—"}</p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Madre</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{motherName}</p>
                  <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                    <Phone className="w-2.5 h-2.5" /> {motherPhone || "—"}
                  </p>
                </div>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Padre</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{fatherName}</p>
                  <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                    <Phone className="w-2.5 h-2.5" /> {fatherPhone}
                  </p>
                </div>
              </div>
            </section>

            {/* Contactos de emergencia */}
            {emergencyContacts.length > 0 && (
              <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="p-0.5 bg-gray-50 rounded">
                    <Heart className="w-3 h-3 text-[#5856d6]" />
                  </div>
                  <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                    Contactos de emergencia
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {emergencyContacts.map((c, index) => (
                    <div key={index} className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5 flex flex-col gap-0.5">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{c.relation}</p>
                      <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{c.name}</p>
                      <p className="text-[10px] text-gray-400 font-medium">{c.phone}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Condiciones médicas */}
            {r.medicalConditions && (
              <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="p-0.5 bg-gray-50 rounded">
                    <Clock className="w-3 h-3 text-[#5856d6]" />
                  </div>
                  <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Condiciones médicas</h4>
                </div>
                <p className="text-[11px] text-[#86868b] whitespace-pre-wrap rounded-md px-2 py-1.5 hover:bg-[#5856d6]/5 transition-colors">{r.medicalConditions}</p>
              </section>
            )}

            {/* Clases seleccionadas */}
            <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="p-0.5 bg-gray-50 rounded">
                  <BookOpen className="w-3 h-3 text-[#5856d6]" />
                </div>
                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Clases seleccionadas</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {selectedClassNames.length === 0 ? (
                  <p className="text-[11px] text-[#86868b]">No hay clases seleccionadas.</p>
                ) : (
                  selectedClassNames.map((name, idx) => (
                    <div key={`${name}-${idx}`} className="p-1.5 bg-gray-50 rounded-md border border-gray-100 flex items-center justify-between transition-colors hover:bg-[#5856d6]/5 hover:border-[#5856d6]/20">
                      <p className="text-[11px] font-bold text-[#161b3d] truncate">{name}</p>
                      <span className="w-1 h-1 rounded-full bg-[#5856d6]/30" />
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* COMPROBANTE FISCAL — same UI as rest of page */}
            {(r.fiscalData || r.paymentFrequency) && showFiscalDetails && (
              <section className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="p-0.5 bg-gray-50 rounded">
                    <CreditCard className="w-3 h-3 text-[#5856d6]" />
                  </div>
                  <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                    Comprobante fiscal
                  </h4>
                </div>

                {r.fiscalData && (
                  <>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-2">Datos fiscales</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Nombre fiscal</p>
                        <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{r.fiscalData.fiscalName || "—"}</p>
                      </div>
                      <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Documento</p>
                        <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{r.fiscalData.documentType || "—"}</p>
                      </div>
                      <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Número</p>
                        <p className="text-[12px] font-bold text-[#161b3d] leading-snug">{r.fiscalData.documentNumber || "—"}</p>
                      </div>
                    </div>

                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-2">Tipo de comprobante</p>
                    <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                      <p className="text-[12px] font-bold text-[#161b3d] leading-snug">
                        {r.fiscalData.requireNCF === false
                          ? "Consumidor Final (sin NCF)"
                          : (() => {
                              const t = r.fiscalData.comprobanteType;
                              const labels: Record<string, string> = {
                                B01: "Crédito Fiscal (B01)",
                                B02: "Consumidor Final (B02)",
                                B14: "Régimen Especial (B14)",
                                B15: "Gubernamental (B15)",
                              };
                              return t ? (labels[t] ?? `${t} (${t})`) : "—";
                            })()}
                      </p>
                    </div>
                  </>
                )}

                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-2">Configuración de pago</p>
                <div className="rounded-md px-2 py-1.5 transition-colors hover:bg-[#5856d6]/5">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Frecuencia de pago</p>
                  <p className="text-[12px] font-bold text-[#161b3d] leading-snug">
                    {r.paymentFrequency === "BIMESTRAL"
                      ? "Bimestral"
                      : r.paymentFrequency === "CUATRIMESTRAL"
                        ? "Cuatrimestral"
                        : r.paymentFrequency ?? "—"}
                  </p>
                </div>
              </section>
            )}

            {/* Resumen y acciones de pago */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg border border-gray-100 p-2.5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-2 transition-colors hover:bg-gray-50/50"
            >
              <div className="flex items-center gap-1.5">
                <div className="p-0.5 bg-gray-50 rounded">
                  <CreditCard className="w-3 h-3 text-[#5856d6]" />
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{totalLabel}</p>
                  <h4 className="text-base font-black text-[#161b3d]">RD$ {totalAmount.toLocaleString()}</h4>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {hasFiscalData && (
                  <button
                    type="button"
                    onClick={() => setShowFiscalDetails((prev) => !prev)}
                    className="text-[#5856d6] text-[9px] font-bold px-2.5 py-1 rounded-md hover:bg-[#5856d6]/5 transition-all border border-[#5856d6]/20"
                  >
                    {showFiscalDetails ? "Ocultar comprobante fiscal" : "Visualizar comprobante fiscal"}
                  </button>
                )}
                {isPaid && (
                  <button
                    type="button"
                    className="bg-[#161b3d] text-white text-[9px] font-bold px-3 py-1 rounded-md hover:bg-[#161b3d]/90 transition-all"
                  >
                    Descargar recibo
                  </button>
                )}
              </div>
            </motion.div>

            {/* Marcar como pagado (manual): cuando el titular paga en la escuela */}
            {(r.status === "awaiting_payment" || r.status === "awaiting_manual_payment") && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
                <p className="text-[11px] text-[#86868b] mb-2">
                  Si el titular pagó en la escuela (efectivo o transferencia), marca el pago como completado para crear el ID del estudiante y enviar el correo.
                </p>
                {markPaidError && (
                  <div className="mb-2 rounded-lg border border-[#8b1e22]/20 bg-[#8b1e22]/5 overflow-hidden">
                    <div className="flex items-start gap-2 px-3 py-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#8b1e22]/10 flex items-center justify-center" aria-hidden>
                        <svg className="w-3 h-3 text-[#8b1e22]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-[#8b1e22] mb-0.5">
                          {markPaidError.includes("CLASS_FULL") || markPaidError.includes("límite de cupos") ? "Límite de cupos" : "Error"}
                        </p>
                        <p className="text-[11px] font-medium text-[#1d1d1f] leading-snug">
                          {markPaidError.replace(/^CLASS_FULL:\s*/i, "").trim()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  disabled={markPaidLoading}
                  onClick={async () => {
                    setMarkPaidError(null);
                    setMarkPaidLoading(true);
                    try {
                      await markRegistrationPaidManually(r.id);
                      setMarkPaidSuccess("Pago marcado como completado. Se creó el estudiante y se envió el correo.");
                      setSelectedRegistrationId(null);
                      load();
                    } catch (err) {
                      setMarkPaidError(err instanceof Error ? err.message : "Error al marcar como pagado.");
                    } finally {
                      setMarkPaidLoading(false);
                    }
                  }}
                  className={`${btnPrimary} px-3 py-2 text-[12px]`}
                >
                  {markPaidLoading ? "Guardando…" : "Marcar como pagado (manual)"}
                </button>
              </div>
            )}
          </div>
        </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className={sectionContainer}
      >
        <div className={sectionPadding}>
        {markPaidSuccess && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[14px] font-medium text-emerald-800">
            {markPaidSuccess}
          </div>
        )}
        <div className="mb-6">
          <h3 className={sectionTitle}>Inscripciones</h3>
          <p className={sectionDescription}>Envíos del formulario público.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end mb-6">
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Estado</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as "" | "pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined")}
              className={`${inputClass} min-w-[140px]`}
            >
              <option value="">Todos</option>
              <option value="pending">Pendiente de pago</option>
              <option value="awaiting_payment">Pendiente de pago (en línea)</option>
              <option value="awaiting_manual_payment">Pendiente de pago (manual)</option>
              <option value="paid">Pagado</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Pago</label>
            <select
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value as "" | "ONLINE" | "LOCAL")}
              className={`${inputClass} min-w-[120px]`}
            >
              <option value="">Todos</option>
              <option value="ONLINE">En línea</option>
              <option value="LOCAL">En escuela</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Comprobante fiscal</label>
            <select
              value={filterFiscal}
              onChange={(e) => setFilterFiscal(e.target.value as "" | "yes" | "no")}
              className={`${inputClass} min-w-[140px]`}
            >
              <option value="">Todos</option>
              <option value="yes">Sí</option>
              <option value="no">No</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Clase</label>
            <select
              value={filterClassId}
              onChange={(e) => setFilterClassId(e.target.value)}
              className={`${inputClass} min-w-[140px]`}
            >
              <option value="">Todas las clases</option>
              {registrationClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Mes (fecha inscripción)</label>
            <input
              type="month"
              value={filterDateMonth}
              onChange={(e) => { setFilterDateMonth(e.target.value); setPage(1); }}
              className={`${inputClass} min-w-[140px]`}
            />
          </div>
          {(filterClassId || filterDateMonth || filterStatus || filterPayment || filterFiscal) && (
            <button
              type="button"
              onClick={() => {
                setFilterStatus("");
                setFilterPayment("");
                setFilterClassId("");
                setFilterDateMonth("");
                setFilterFiscal("");
              }}
              className="text-[13px] font-bold text-[#5856D6] hover:underline self-end pb-2.5"
            >
              Limpiar filtros
            </button>
          )}
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exportExcelRecords.length === 0}
            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-bold text-[13px] hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Exportar Excel
          </button>
        </div>
        <ul className="space-y-2">
          {filteredByDate.length === 0 ? (
            <li className="text-[13px] text-[#86868b]">Ninguna inscripción coincide con los filtros.</li>
          ) : (
            paginatedRegistrations.map((r) => (
              <li key={r.id} className="rounded-xl border border-gray-100 bg-[#f5f5f7]/50 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSelectedRegistrationId(r.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#f5f5f7] transition-colors"
                >
                  <span className="text-[14px] font-bold text-[#1d1d1f]">{r.child?.childName ?? r.id}</span>
                  <span className="text-[13px] text-[#86868b]">
                    {r.paymentMethod} · RD$ {r.totalPrice?.toLocaleString() ?? 0} · {adminStatusLabel(r.status)}
                    {r.studentCode ? ` · ${r.studentCode}` : ""} · {r.paymentFrequency ?? "—"}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        {filteredByDate.length > REGISTRATIONS_PAGE_SIZE && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
            <p className="text-[13px] text-[#86868b]">
              Mostrando {(currentPage - 1) * REGISTRATIONS_PAGE_SIZE + 1}–{Math.min(currentPage * REGISTRATIONS_PAGE_SIZE, filteredByDate.length)} de {filteredByDate.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className={`${btnPrimary} py-2 disabled:opacity-50`}
              >
                Anterior
              </button>
              <span className="text-[13px] font-medium text-[#1d1d1f] px-2">
                Página {currentPage} de {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className={`${btnPrimary} py-2 disabled:opacity-50`}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// =============================================================================
// STUDENTS: Ver estudiantes por mes (ya pagados)
// =============================================================================

export function AdminStudentsView() {
  const [students, setStudents] = useState<StudentForAdmin[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterMonth, setFilterMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const [managedStudentId, setManagedStudentId] = useState<string | null>(null);
  const [studentEnrollments, setStudentEnrollments] = useState<StudentEnrollmentRow[]>([]);
  const [studentEnrollmentsName, setStudentEnrollmentsName] = useState("");
  const [classesForDropdown, setClassesForDropdown] = useState<Array<{ id: string; name: string }>>([]);
  const [manageClassesLoading, setManageClassesLoading] = useState(false);
  const [addClassId, setAddClassId] = useState("");
  const [creatingEnrollment, setCreatingEnrollment] = useState(false);
  const [deletingEnrollmentId, setDeletingEnrollmentId] = useState<string | null>(null);
  const [manageClassesError, setManageClassesError] = useState<string | null>(null);

  const loadStudents = useCallback(() => {
    setStudentsLoading(true);
    listStudentsForAdmin({ month: filterMonth || undefined })
      .then((res) => setStudents(res.students))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [filterMonth]);

  useEffect(() => {
    setStudentsLoading(true);
    listStudentsForAdmin({ month: filterMonth || undefined })
      .then((res) => setStudents(res.students))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [filterMonth]);

  useEffect(() => {
    if (!managedStudentId) {
      setStudentEnrollments([]);
      setStudentEnrollmentsName("");
      setClassesForDropdown([]);
      setManageClassesError(null);
      return;
    }
    setManageClassesLoading(true);
    setManageClassesError(null);
    Promise.all([
      getStudentWithEnrollments(managedStudentId),
      getClassesWithEnrollments(),
    ])
      .then(([enrRes, classesRes]) => {
        setStudentEnrollments(enrRes.enrollments);
        setStudentEnrollmentsName(enrRes.childName);
        setClassesForDropdown(
          classesRes.classes.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => setManageClassesError(err instanceof Error ? err.message : "Error al cargar."))
      .finally(() => setManageClassesLoading(false));
  }, [managedStudentId]);

  const filteredStudents = students.filter(
    (s) => !filterName.trim() || (s.name ?? s.id).toLowerCase().includes(filterName.trim().toLowerCase())
  );
  const displayedStudents = filteredStudents.slice(0, MAX_STUDENTS_DISPLAY);
  const isCapped = filteredStudents.length > MAX_STUDENTS_DISPLAY;
  const monthOptions = getMonthOptions();

  return (
    <div className={sectionContainer}>
      <div className={sectionPadding}>
        <div className="mb-6">
          <h3 className={sectionTitle}>Estudiantes</h3>
          <p className={sectionDescription}>
            Estudiantes por mes: ver qué estudiantes se inscribieron (pago completado) en cada mes.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="space-y-1.5 min-w-[160px]">
            <label className={`block ${inputLabel}`}>Mes</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className={inputClass}
            >
              <option value="">Todos los meses</option>
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 min-w-[200px]">
            <label className={`block ${inputLabel}`}>Buscar por nombre</label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder=""
              className={inputClass}
            />
          </div>
          <button type="button" onClick={loadStudents} disabled={studentsLoading} className={`${btnPrimary} px-4 py-3`}>
            {studentsLoading ? "Cargando…" : "Actualizar lista"}
          </button>
          <button
            type="button"
            onClick={() => exportEstudiantesToExcel(filteredStudents, { month: filterMonth || undefined })}
            disabled={filteredStudents.length === 0}
            className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:pointer-events-none"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
        {!studentsLoading && (filteredStudents.length > 0 || students.length > 0) && (
          <p className="text-[12px] text-[#86868b] mb-3">
            {isCapped
              ? `Mostrando los primeros ${MAX_STUDENTS_DISPLAY} de ${filteredStudents.length}. Usa el filtro para acotar.`
              : `${filteredStudents.length} estudiante${filteredStudents.length !== 1 ? "s" : ""}${filterMonth ? " en este mes." : "."}`}
          </p>
        )}
        {studentsLoading && students.length === 0 ? (
          <p className="text-[13px] text-[#86868b]">Cargando estudiantes…</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-100 bg-[#f5f5f7]/50 text-left">
                  <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Nombre</th>
                  <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">ID</th>
                  <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Clases</th>
                </tr>
              </thead>
              <tbody>
                {displayedStudents.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 px-4 text-[13px] text-[#86868b]">
                      {students.length === 0 ? "Aún no hay estudiantes con pago completado." : "Ningún estudiante coincide con el mes o el filtro."}
                    </td>
                  </tr>
                ) : (
                  displayedStudents.map((s) => (
                    <tr key={s.id} className="border-b border-gray-100 last:border-0 hover:bg-[#f5f5f7]/30 transition-colors">
                      <td className="py-3 px-4 font-bold text-[#1d1d1f]">{s.name ?? "—"}</td>
                      <td className="py-3 px-4 text-[#86868b] font-mono text-[12px]">{s.id}</td>
                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => setManagedStudentId(s.id)}
                          className="text-[12px] font-bold text-[#5856D6] hover:underline"
                            >
                              Gestionar clases
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
        )}

        {/* Modal: Gestionar clases del estudiante */}
        {managedStudentId && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manage-classes-title"
              >
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center justify-between gap-2">
                      <h3 id="manage-classes-title" className={subsectionTitle}>
                        Clases de {studentEnrollmentsName || "…"}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setManagedStudentId(null)}
                        className="text-[13px] font-bold text-[#86868b] hover:text-[#1d1d1f]"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                  <div className="p-5 overflow-y-auto flex-1">
                    {manageClassesLoading ? (
                      <p className="text-[13px] text-[#86868b]">Cargando…</p>
                    ) : manageClassesError ? (
                      <p className="text-[13px] font-medium text-[#8b1e22]">{manageClassesError}</p>
                    ) : (
                      <>
                        <p className="text-[12px] font-bold text-[#86868b] uppercase tracking-wide mb-2">Clases actuales</p>
                        {studentEnrollments.length === 0 ? (
                          <p className="text-[13px] text-[#86868b] mb-4">Sin clases asignadas.</p>
                        ) : (
                          <ul className="space-y-2 mb-4">
                            {studentEnrollments.map((en) => (
                              <li
                                key={en.enrollmentId}
                                className="flex items-center justify-between py-2 px-3 rounded-xl bg-[#f5f5f7] border border-gray-100"
                              >
                                <span className="text-[13px] font-medium text-[#1d1d1f]">{en.className}</span>
                                <button
                                  type="button"
                                  disabled={deletingEnrollmentId === en.enrollmentId}
                                  onClick={async () => {
                                    setManageClassesError(null);
                                    setDeletingEnrollmentId(en.enrollmentId);
                                    try {
                                      await deleteEnrollment({ studentId: managedStudentId, enrollmentId: en.enrollmentId });
                                      setStudentEnrollments((prev) => prev.filter((e) => e.enrollmentId !== en.enrollmentId));
                                    } catch (err) {
                                      setManageClassesError(err instanceof Error ? err.message : "Error al eliminar.");
                                    } finally {
                                      setDeletingEnrollmentId(null);
                                    }
                                  }}
                                  className="text-[12px] font-bold text-red-600 hover:underline disabled:opacity-50"
                                >
                                  {deletingEnrollmentId === en.enrollmentId ? "…" : "Eliminar"}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="text-[12px] font-bold text-[#86868b] uppercase tracking-wide mb-2">Añadir clase</p>
                        <div className="flex gap-2">
                          <select
                            value={addClassId}
                            onChange={(e) => setAddClassId(e.target.value)}
                            className={`${inputClass} flex-1 min-w-0`}
                          >
                            <option value="">Seleccionar clase</option>
                            {classesForDropdown
                              .filter((c) => !studentEnrollments.some((e) => e.classId === c.id))
                              .map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                          </select>
                          <button
                            type="button"
                            disabled={!addClassId || creatingEnrollment}
                            onClick={async () => {
                              if (!addClassId) return;
                              setManageClassesError(null);
                              setCreatingEnrollment(true);
                              try {
                                const now = new Date();
                                const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                                await createEnrollment({ childId: managedStudentId, classId: addClassId, month });
                                const res = await getStudentWithEnrollments(managedStudentId);
                                setStudentEnrollments(res.enrollments);
                                setAddClassId("");
                              } catch (err) {
                                setManageClassesError(err instanceof Error ? err.message : "Error al añadir.");
                              } finally {
                                setCreatingEnrollment(false);
                              }
                            }}
                            className={`${btnPrimary} px-4 py-3`}
                          >
                            {creatingEnrollment ? "…" : "Añadir"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
      </div>
    </div>
  );
}

// =============================================================================
// PAYMENTS (registration payment status)
// =============================================================================

function getDefaultMonth(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Format Firestore Timestamp (seconds or _seconds when serialized from callable). */
function formatTransactionDate(paidAt: { seconds?: number; _seconds?: number; nanoseconds?: number; _nanoseconds?: number } | null | undefined): string {
  if (!paidAt) return "—";
  const sec = "seconds" in paidAt && paidAt.seconds != null ? paidAt.seconds : (paidAt as { _seconds?: number })._seconds;
  if (sec == null || typeof sec !== "number") return "—";
  const date = new Date(sec * 1000);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" });
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    const value = `${y}-${String(m + 1).padStart(2, "0")}`;
    options.push({ value, label: `${monthNames[m]} ${y}` });
  }
  return options;
}

/** Options for Active Registration Month: current month + next 11 (future). Full month names. */
function getRegistrationMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    const value = `${y}-${String(m + 1).padStart(2, "0")}`;
    options.push({ value, label: `${monthNames[m]} ${y}` });
  }
  return options;
}

export function AdminSettingsView() {
  const [activeMonth, setActiveMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingMonth, setSavingMonth] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin settings (beyond active month)
  const [allowOnlinePayment, setAllowOnlinePayment] = useState(true);
  const [allowManualPayment, setAllowManualPayment] = useState(true);
  const [enableFiscalReceipts, setEnableFiscalReceipts] = useState(true);
  const [defaultComprobanteType, setDefaultComprobanteType] = useState<AdminSettings["defaultComprobanteType"]>("B02");
  const [allowFiscalRequest, setAllowFiscalRequest] = useState(true);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [supportEmail, setSupportEmail] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");

  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);

  const initialSettingsRef = useRef<AdminSettings | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    setSettingsError(null);
    Promise.all([getActiveRegistrationMonth(), getAdminSettings()])
      .then(([monthRes, settingsRes]) => {
        setActiveMonth(monthRes.month);
        const s = settingsRes.settings;
        initialSettingsRef.current = s;
        setAllowOnlinePayment(s.allowOnlinePayment);
        setAllowManualPayment(s.allowManualPayment);
        setEnableFiscalReceipts(s.enableFiscalReceipts);
        setDefaultComprobanteType(s.defaultComprobanteType);
        setAllowFiscalRequest(s.allowFiscalRequest);
        setRegistrationOpen(s.registrationOpen);
        setSupportEmail(s.supportEmail ?? "");
        setSupportPhone(s.supportPhone ?? "");
        setSchoolAddress(s.schoolAddress ?? "");
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "No se pudo cargar la configuración.")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const baseMonthOptions = useMemo(() => getRegistrationMonthOptions(), []);
  const registrationMonthOptions = useMemo(() => {
    if (activeMonth && !baseMonthOptions.some((o) => o.value === activeMonth)) {
      const [y, m] = activeMonth.split("-").map(Number);
      if (y && m >= 1 && m <= 12) {
        const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        return [{ value: activeMonth, label: `${monthNames[m - 1]} ${y}` }, ...baseMonthOptions];
      }
    }
    return baseMonthOptions;
  }, [baseMonthOptions, activeMonth]);

  const handleMonthChange = async (value: string) => {
    if (activeMonth === value) return;
    setSavingMonth(true);
    setError(null);
    try {
      await setActiveRegistrationMonth(value);
      setActiveMonth(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo actualizar el mes.");
    } finally {
      setSavingMonth(false);
    }
  };

  const handleSaveAllSettings = async () => {
    if (!initialSettingsRef.current) return;
    const original = initialSettingsRef.current;
    const current: AdminSettings = {
      allowOnlinePayment,
      allowManualPayment,
      enableFiscalReceipts,
      defaultComprobanteType,
      allowFiscalRequest,
      registrationOpen,
      supportEmail: supportEmail.trim(),
      supportPhone: supportPhone.trim(),
      schoolAddress: schoolAddress.trim(),
    };

    const updates: Partial<AdminSettings> = {};
    (Object.keys(current) as (keyof AdminSettings)[]).forEach((key) => {
      if (current[key] !== original[key]) {
        (updates as Record<string, AdminSettings[keyof AdminSettings]>)[key as string] =
          current[key];
      }
    });

    if (Object.keys(updates).length === 0) {
      setSettingsSuccess("No hay cambios para guardar.");
      return;
    }

    setSettingsSaving(true);
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      const res = await updateAdminSettings(updates);
      const saved = res.settings;
      initialSettingsRef.current = saved;
      setAllowOnlinePayment(saved.allowOnlinePayment);
      setAllowManualPayment(saved.allowManualPayment);
      setEnableFiscalReceipts(saved.enableFiscalReceipts);
      setDefaultComprobanteType(saved.defaultComprobanteType);
      setAllowFiscalRequest(saved.allowFiscalRequest);
      setRegistrationOpen(saved.registrationOpen);
      setSupportEmail(saved.supportEmail ?? "");
      setSupportPhone(saved.supportPhone ?? "");
      setSchoolAddress(saved.schoolAddress ?? "");
      setSettingsSuccess("Configuración guardada correctamente");
    } catch (err) {
      setSettingsError(
        err instanceof Error ? err.message : "No se pudo guardar la configuración."
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  return (
    <div className={sectionContainer}>
      <div className={sectionPadding}>
        <div className="mb-6">
          <h3 className={sectionTitle}>Configuración</h3>
          <p className={sectionDescription}>Período de inscripción y ajustes administrativos.</p>
        </div>

        {/* 1. Período de inscripción */}
        <div className="rounded-2xl border border-gray-100 bg-[#f5f5f7]/60 px-4 py-3 mb-8">
          <h4 className="text-[14px] font-bold text-[#1d1d1f] mb-2">Período de inscripción</h4>
          <p className="text-[12px] text-[#86868b] font-medium mb-3">
            El mes que se asigna a las nuevas inscripciones. Las familias verán este mes en el formulario y los ciclos de facturación usarán este valor al confirmar el pago.
          </p>
          {error && <p className="text-[12px] text-red-600 mb-2">{error}</p>}
          {loading ? (
            <p className="text-[13px] text-[#86868b]">Cargando…</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <label className={`block ${inputLabel}`}>Mes activo de inscripción</label>
              <select
                value={activeMonth ?? ""}
                onChange={(e) => handleMonthChange(e.target.value)}
                disabled={savingMonth || activeMonth === null}
                className={`${inputClass} min-w-[180px]`}
              >
                {registrationMonthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {savingMonth && <span className="text-[13px] text-[#86868b]">Guardando…</span>}
            </div>
          )}
        </div>

        {/* Facturación fiscal */}
        <div className="rounded-2xl border border-gray-100 bg-[#f5f5f7]/60 px-4 py-4 mb-5">
          <h4 className="text-[14px] font-bold text-[#1d1d1f] mb-2">Facturación fiscal</h4>
          <p className="text-[12px] text-[#86868b] font-medium mb-3">
            Controla cómo se manejan los comprobantes fiscales durante el pago.
          </p>
          {loading ? (
            <p className="text-[13px] text-[#86868b]">Cargando…</p>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-[13px] text-[#1d1d1f]">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={enableFiscalReceipts}
                  onChange={(e) => setEnableFiscalReceipts(e.target.checked)}
                />
                <span className="font-medium">Emitir comprobante fiscal</span>
              </label>

              <div className="space-y-1.5 max-w-xs">
                <label className={`block ${inputLabel}`}>Tipo de comprobante por defecto</label>
                <select
                  value={defaultComprobanteType}
                  onChange={(e) =>
                    setDefaultComprobanteType(e.target.value as AdminSettings["defaultComprobanteType"])
                  }
                  className={inputClass}
                >
                  <option value="B01">B01 Crédito Fiscal</option>
                  <option value="B02">B02 Consumidor Final</option>
                  <option value="B14">B14 Régimen Especial</option>
                  <option value="B15">B15 Gubernamental</option>
                </select>
              </div>

              <label className="flex items-center gap-2 text-[13px] text-[#1d1d1f]">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={allowFiscalRequest}
                  onChange={(e) => setAllowFiscalRequest(e.target.checked)}
                />
                <span className="font-medium">Permitir que el cliente solicite comprobante fiscal</span>
              </label>
            </div>
          )}
        </div>

        {/* 4. Control de inscripciones */}
        <div className="rounded-2xl border border-gray-100 bg-[#f5f5f7]/60 px-4 py-4 mb-5">
          <h4 className="text-[14px] font-bold text-[#1d1d1f] mb-2">Control de inscripciones</h4>
          <p className="text-[12px] text-[#86868b] font-medium mb-3">
            Permite abrir o cerrar el formulario público de inscripción.
          </p>
          {loading ? (
            <p className="text-[13px] text-[#86868b]">Cargando…</p>
          ) : (
            <label className="flex items-center gap-2 text-[13px] text-[#1d1d1f]">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={registrationOpen}
                onChange={(e) => setRegistrationOpen(e.target.checked)}
              />
              <span className="font-medium">Permitir nuevas inscripciones</span>
            </label>
          )}
        </div>

        {/* 5. Información de contacto */}
        <div className="rounded-2xl border border-gray-100 bg-[#f5f5f7]/60 px-4 py-4 mb-6">
          <h4 className="text-[14px] font-bold text-[#1d1d1f] mb-2">Información de contacto</h4>
          <p className="text-[12px] text-[#86868b] font-medium mb-3">
            Datos que se mostrarán en comprobantes y comunicaciones de pago.
          </p>
          {loading ? (
            <p className="text-[13px] text-[#86868b]">Cargando…</p>
          ) : (
            <div className="space-y-3 max-w-xl">
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Correo de soporte</label>
                <input
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Teléfono</label>
                <input
                  type="tel"
                  value={supportPhone}
                  onChange={(e) => setSupportPhone(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <label className={`block ${inputLabel}`}>Dirección</label>
                <textarea
                  value={schoolAddress}
                  onChange={(e) => setSchoolAddress(e.target.value)}
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Save all settings */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1">
            {settingsError && (
              <p className="text-[12px] text-red-600">{settingsError}</p>
            )}
            {settingsSuccess && !settingsError && (
              <p className="text-[12px] text-emerald-600">{settingsSuccess}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleSaveAllSettings}
            disabled={loading || settingsSaving || !initialSettingsRef.current}
            className={`${btnPrimary} px-5 py-3`}
          >
            {settingsSaving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPaymentsView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [month, setMonth] = useState<string>(getDefaultMonth);

  const load = () => {
    setLoading(true);
    setError(null);
    listTransactions({ month })
      .then((res) => setTransactions(res.transactions))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [month]);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  if (loading && transactions.length === 0) {
    return (
      <div className={sectionContainer}>
        <div className={sectionPadding}>
          <p className="text-[13px] text-[#86868b]">Cargando pagos…</p>
        </div>
      </div>
    );
  }
  if (error && transactions.length === 0) {
    return (
      <div className={sectionContainer}>
        <div className={sectionPadding}>
          <p className="text-[13px] text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={sectionContainer}>
      <div className={sectionPadding}>
        <div className="mb-6">
          <h3 className={sectionTitle}>Pagos</h3>
          <p className={sectionDescription}>Transacciones completadas por mes. Selecciona el mes para ver los pagos (en línea o manual).</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end mb-6">
          <div className="space-y-1.5">
            <label className={`block ${inputLabel}`}>Mes</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={`${inputClass} min-w-[140px]`}
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button type="button" onClick={load} className={`${btnPrimary} px-4 py-3`}>
            Actualizar
          </button>
          <button
            type="button"
            onClick={() => exportTransactionsToExcel(transactions, { month })}
            disabled={transactions.length === 0}
            className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:pointer-events-none"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100 bg-[#f5f5f7]/50 text-left">
                <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Estudiante</th>
                <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Monto</th>
                <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Método</th>
                <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Origen</th>
                <th className="py-3 px-4 text-[12px] font-bold text-[#1d1d1f]">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan={5} className="py-6 px-4 text-[13px] text-[#86868b]">No hay transacciones en este mes.</td></tr>
              ) : (
                transactions.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 last:border-0 hover:bg-[#f5f5f7]/30 transition-colors">
                    <td className="py-3 px-4 font-bold text-[#1d1d1f]">{t.studentName || "—"}</td>
                    <td className="py-3 px-4 text-[#86868b]">RD$ {(t.amount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-[#86868b]">{t.paymentMethod || "—"}</td>
                    <td className="py-3 px-4 text-[#86868b]">{t.source === "manual" ? "Manual" : "En línea"}</td>
                    <td className="py-3 px-4 text-[#86868b]">{formatTransactionDate(t.paidAt ?? null)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
